import isEqual from 'lodash/isEqual';
import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  Measure,
  MeasureDataResponse,
  MeasureMetadata,
} from '../../../../../api/work-periods-client/work-periods-client.type';
import { useDeepCompareEffect } from '../../../../../hooks/useDeepCompareEffect';
import { useProcessAnalysisStore } from '../../../../../store/process-analysis-store/process-analysis-store';
import {
  setActivePanel,
  setSelectedDataPoint,
} from '../../../../../store/process-analysis-store/process-analysis-store.actions';
import {
  useSelectedMeasures,
  useSelectedMeasureTrends,
} from '../../../../../store/process-analysis-store/process-analysis-store.hooks';
import { Panel } from '../../../../../store/process-analysis-store/process-analysis-store.type';
import { LineChart } from '../../../components/line-chart/line-chart';
import { shouldDisplayValue } from '../../../components/line-chart/line-chart.helpers';
import { useItemColors } from '../../../components/line-chart/line-chart.hooks';
import type { ChartDataItem } from '../../../components/line-chart/line-chart.type';
import { formatMeasureName, getUnitsLabel } from '../../../process-analysis.helpers';
import { useChartData, useTargetLineItems } from './line-chart.hooks';

type Props = {
  data: MeasureDataResponse;
  mergedMeasureMetadata: MeasureMetadata[];
};

export const MeasureComparisonLineChart = forwardRef<HTMLDivElement, Props>(({ data, mergedMeasureMetadata }, ref) => {
  const { t } = useTranslation();
  const availableMeasures = useProcessAnalysisStore((state) => state.availableMeasures);
  const measures = useSelectedMeasures();
  const trends = useSelectedMeasureTrends();
  const selectedDataPoint = useProcessAnalysisStore((state) => state.selectedDataPoint);

  const chartData = useChartData(data, mergedMeasureMetadata);
  const [hoveredMeasure, setHoveredMeasure] = useState<string | null>(null);

  const selectedItems = measures.map((measure) => ({ id: measure, name: measure }));
  const selectedTrends = trends.map((measure) => ({ id: measure, name: measure }));

  const itemColors = useItemColors(selectedItems);
  const selectedTargets = useTargetLineItems(mergedMeasureMetadata, itemColors);

  // Update selectedDataPoint when mergedMeasureMetadata changes (e.g., when new targets are added)
  useDeepCompareEffect(() => {
    if (selectedDataPoint && data) {
      const measureMetadata = mergedMeasureMetadata.find((m) => m.measure_name === selectedDataPoint.measure);

      if (measureMetadata) {
        const updatedTargets = measureMetadata.targets || [];
        // Only update if targets have actually changed
        if (!isEqual(updatedTargets, selectedDataPoint.targets)) {
          setSelectedDataPoint({
            ...selectedDataPoint,
            targets: updatedTargets,
          });
        }
      }
    }
  }, [mergedMeasureMetadata, selectedDataPoint, data]);

  const handleChartPointClick = (itemId: string, payload: ChartDataItem) => {
    if (!itemId || !payload || !data) {
      return;
    }

    try {
      const value = payload[itemId] as number | null;
      const measureData = data[itemId as Measure] || {};
      const measureMetadata = mergedMeasureMetadata.find((m) => m.measure_name === itemId);
      const targets = measureMetadata?.targets || [];

      setSelectedDataPoint({
        measure: itemId as Measure,
        date: payload.date || '',
        rawDate: payload.rawDate || '',
        value: value ?? null,
        measureData,
        targets,
      });
      setActivePanel(Panel.Side);
    } catch (error) {
      console.error('Failed to set selected data point:', error);
    }
  };

  return (
    <LineChart
      ref={ref}
      chartData={chartData}
      selectedItems={selectedItems}
      selectedTrends={selectedTrends}
      selectedTargets={selectedTargets}
      hoveredItem={hoveredMeasure}
      setHoveredItem={setHoveredMeasure}
      onPointClick={handleChartPointClick}
      yAxisConfig={{
        label: t('common.units'),
      }}
      formatItemName={(measure) => formatMeasureName(measure, t)}
      formatValue={(value, measureName) => {
        const units = availableMeasures.find((measure) => measure.measure_name === measureName)?.measure_units;
        const unitsLabel = units ? getUnitsLabel(units, t) : t('common.units');

        return shouldDisplayValue(value) ? `${value} ${unitsLabel}` : t('common.labels.not_available');
      }}
    />
  );
});
